/**
 * Shared markdown helpers for rendering API text (e.g. user-story
 * acceptance criteria) as HTML in the UI.
 */

const MARKDOWN_FIELD_KEYS = new Set([
  "acceptancecriteria",
  "criteria",
]);

/** True when the field is known to arrive as markdown (acceptance criteria). */
export function isMarkdownField(mapping?: string | null): boolean {
  if (!mapping) return false;
  const key = mapping.toLowerCase().replace(/[_-]/g, "");
  return MARKDOWN_FIELD_KEYS.has(key);
}

/** True when the value looks like markdown (bold, lists, escaped newlines). */
export function looksLikeMarkdown(value: unknown): boolean {
  const text = normalizeMarkdownSource(value);
  if (!text) return false;
  return /(\*\*[^*]+\*\*)|(^|\n)\s*[\*\-]\s+\S/.test(text);
}

export function shouldRenderMarkdown(
  value: unknown,
  mapping?: string | null
): boolean {
  return isMarkdownField(mapping) || looksLikeMarkdown(value);
}

/**
 * Convert markdown (or an array of markdown strings) to safe-to-sanitize HTML.
 * Handles escaped `\n` / `\"` from JSON payloads, `**bold**`, `*italic*`,
 * `* / -` bullets, numbered lists (with nested bullets), and inline `code`.
 */
export function markdownToHtml(value: unknown): string {
  const source = normalizeMarkdownSource(value);
  if (!source) return "";

  const escaped = escapeHtml(source);
  const lines = escaped.split("\n");
  const html: string[] = [];
  let listTag: "ul" | "ol" | null = null;
  let nestedUlOpen = false;
  let openLi = false;

  const closeNestedUl = (): void => {
    if (nestedUlOpen) {
      html.push("</ul>");
      nestedUlOpen = false;
    }
  };

  const closeLi = (): void => {
    closeNestedUl();
    if (openLi) {
      html.push("</li>");
      openLi = false;
    }
  };

  const closeList = (): void => {
    closeLi();
    if (listTag) {
      html.push(`</${listTag}>`);
      listTag = null;
    }
  };

  const openList = (tag: "ul" | "ol"): void => {
    if (listTag !== tag) {
      closeList();
      html.push(`<${tag}>`);
      listTag = tag;
    }
  };

  for (const rawLine of lines) {
    const ulMatch = rawLine.match(/^(\s*)[\*\-\+]\s+(.*)$/);
    const olMatch = rawLine.match(/^(\s*)\d+[.)]\s+(.*)$/);

    // Nested bullets under a numbered item (indented)
    if (ulMatch && listTag === "ol" && (ulMatch[1]?.length ?? 0) > 0) {
      if (!openLi) {
        continue;
      }
      if (!nestedUlOpen) {
        html.push("<ul>");
        nestedUlOpen = true;
      }
      for (const item of splitInlineBulletItems(ulMatch[2])) {
        html.push(`<li>${applyInlineMarkdown(item)}</li>`);
      }
      continue;
    }

    if (ulMatch) {
      openList("ul");
      closeLi();
      for (const item of splitInlineBulletItems(ulMatch[2])) {
        html.push(`<li>${applyInlineMarkdown(item)}</li>`);
      }
      continue;
    }

    if (olMatch) {
      openList("ol");
      closeLi();
      html.push(`<li>${applyInlineMarkdown(olMatch[2].trim())}`);
      openLi = true;
      continue;
    }

    if (listTag && /^\s+\S/.test(rawLine)) {
      if (openLi) {
        html.push(`<br>${applyInlineMarkdown(rawLine.trim())}`);
      } else {
        const last = html.length - 1;
        if (last >= 0) {
          html[last] = html[last].replace(
            /<\/li>$/,
            `<br>${applyInlineMarkdown(rawLine.trim())}</li>`
          );
        }
      }
      continue;
    }

    if (!rawLine.trim()) {
      // Keep numbered item open across blank lines before nested bullets
      if (listTag === "ol" && openLi) {
        continue;
      }
      closeList();
      continue;
    }

    closeList();
    html.push(`<p>${applyInlineMarkdown(rawLine.trim())}</p>`);
  }

  closeList();
  return html.join("");
}

function normalizeMarkdownSource(value: unknown): string {
  if (value == null) return "";

  const asText = Array.isArray(value)
    ? value
        .map((item) => String(item ?? "").trim())
        .filter(Boolean)
        .map((item) => (/^\s*[\*\-\+]/.test(item) ? item : `* ${item}`))
        .join("\n")
    : String(value);

  return asText
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "  ")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replace(/\r\n/g, "\n");
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** Split `"a * b * c"` into `["a", "b", "c"]` without breaking `**bold**`. */
function splitInlineBulletItems(text: string): string[] {
  return text
    .split(/\s+\*(?!\*)\s+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function applyInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/(^|[^\*])\*(?!\s)([^*]+?)\*(?!\*)/g, "$1<em>$2</em>");
}
